import { useMemo } from 'react';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowForward } from '@mui/icons-material';
import { Button } from '@uplift/ui-v2';

import { StepContainer, StepContent, StepHeading, StepNavigator, WaveBg } from '../../components';
import { AssessmentResultNextData, OnboardingMachineContext } from '../types';
import { CareType } from '@uplift/api';

export type AssessmentResultProps = {
  onNext: (data: AssessmentResultNextData) => void;
  onBack: () => void;
  onChooseAnother: () => void;
  answers: Pick<OnboardingMachineContext,
  | 'phqScore1'
  | 'phqScore2'
  | 'gadScore1'
  | 'gadScore2'
  | 'hasMedHistory'
  | 'hasTherapist'
  | 'isTherapistGoodFit'>;
};

export const AssessmentResult: React.VFC<AssessmentResultProps> = props => {
  const { classes } = useAssessmentResultStyles();

  const recommendedLevelOfCare = useMemo((): CareType => {
    const { phqScore1, phqScore2, gadScore1, gadScore2, hasMedHistory, hasTherapist, isTherapistGoodFit } = props.answers;

    const isPhqHigh = phqScore1 + phqScore2 >= 3;
    const isGadHigh = gadScore1 + gadScore2 >= 3;
    const isScoreHigh = isPhqHigh || isGadHigh;

    if (hasMedHistory || isScoreHigh) {
      if (hasTherapist === false) {
        return CareType.therapyAndPsychiatry;
      }

      if (hasTherapist && !isTherapistGoodFit) {
        return CareType.therapyAndPsychiatry;
      }

      if (hasTherapist && isTherapistGoodFit) {
        return CareType.psychiatry;
      }
    }

    return isScoreHigh ? CareType.psychiatry : CareType.therapy;
  }, [ props.answers ]);

  const onSelectRecommended = () => props.onNext({ careType: recommendedLevelOfCare });

  if (recommendedLevelOfCare === CareType.therapyAndPsychiatry) {
    return (
      <AssessmentResultTemplate
        buttonLabel="care team"
        color="blue"
        onBack={props.onBack}
        onContinue={onSelectRecommended}
        onChooseAnother={props.onChooseAnother}
        stepHeading={(
          <StepHeading>
            We recommend creating a&nbsp;
            <Typography
              component="span"
              variant="inherit"
              className={classes.blueColor}
            >
              care team
            </Typography>
          </StepHeading>
        )}
      >
        <div className={classes.sectionContent}>
          <div className={classes.subSection}>
            <Typography variant="h6">What is a care team?</Typography>
            <Typography>
              A care team is made up of a <b>therapist</b> and a <b>psychiatric provider</b>. They can share with each other their clinical notes on UpLift and communicate. By working together, your providers have a fuller picture of your mental health—so they can make better decisions about your care and how to support you towards your goals.
            </Typography>
          </div>
          <div className={classes.subSection}>
            <Typography variant="h6">What is the benefit of having a care team?</Typography>
            <Typography>
              Both therapy and medication can provide you with mental health tools. Each helps you unlock and use those tools to manage your well-being.
              <br /><br />
              Therapists spend more time with you. Sessions are longer and more frequent. Through therapy, you can develop coping strategies, insights, and skills that last a lifetime—even if you stop using medication.
              <br /><br />
              Psychiatric providers understand brain and body chemistry, and can prescribe medication when necessary. Medication can alleviate some symptoms, stabilize your mood, and make therapy more effective.
            </Typography>
          </div>
          <div className={classes.subSection}>
            <Typography variant="h6">What you can expect</Typography>
            <Typography>
              You’ll start by booking sessions for therapy and for medication management. After, you can expect follow-up sessions with each provider, to track your progress.
              <br /><br />
              You and a therapist will usually begin with weekly sessions—to set goals and make a plan to reach them. In sessions, you’ll discuss your experiences, learn about yourself, and how to achieve your goals.
              <br /><br />
              Medication management starts with a consultation with a psychiatric provider to learn what kind of treatment is right for you. If you move forward with medication, you’ll continue with sessions to see whether it’s helping you towards your goals or if you need to change treatment.
            </Typography>
          </div>
        </div>
      </AssessmentResultTemplate>
    );
  }

  if (recommendedLevelOfCare === CareType.psychiatry) {
    return (
      <AssessmentResultTemplate
        careType="medication management"
        buttonLabel="med management"
        color="primary"
        onBack={props.onBack}
        onContinue={onSelectRecommended}
        onChooseAnother={props.onChooseAnother}
      >
        <div className={classes.sectionContent}>
          <div className={classes.subSection}>
            <Typography variant="h6">Impacts to your daily life</Typography>
            <Typography>
              Your responses suggest you’re experiencing symptoms that impact your daily life—your relationships, responsibilities, hobbies, and more.
            </Typography>
          </div>
          <div className={classes.subSection}>
            <Typography variant="h6">What you can expect</Typography>
            <Typography>
              Medication management starts with a consultation with a psychiatric provider to learn what kind of treatment is right for you. Together, you’ll create a plan and meet on a regular basis to track your progress.
            </Typography>
          </div>
        </div>
      </AssessmentResultTemplate>
    );
  }

  return (
    <AssessmentResultTemplate
      careType="talk therapy"
      color="secondary"
      onBack={props.onBack}
      onContinue={onSelectRecommended}
      onChooseAnother={props.onChooseAnother}
    >
      <div className={classes.sectionContent}>
        <div className={classes.subSection}>
          <Typography variant="h6">Start with a therapist</Typography>
          <Typography>
            Talk to them about what you’re experiencing and what it means for your mental health.
            <br /><br />
            Your answers suggest you might not need medication—but if you experience different symptoms from what we asked about, you can share that with your therapist.
          </Typography>
        </div>
        <div className={classes.subSection}>
          <Typography variant="h6">What you can expect</Typography>
          <Typography>
            You’ll start by meeting on a regularly—usually weekly—to set goals and make a plan to reach them. In sessions, you’ll discuss your experiences and track your progress to learn about yourself and what you need to achieve your goals.
          </Typography>
        </div>
      </div>
    </AssessmentResultTemplate>
  );
};

type AssessmentResultTemplateProps = {
  buttonLabel?: string;
  color: 'primary' | 'secondary' | 'blue';
  onBack: () => void;
  onContinue: () => void;
  onChooseAnother: () => void;
  children: React.ReactNode;
} & ({
  careType: string;
  stepHeading?: never;
} | {
  careType?: never;
  stepHeading: React.ReactNode;
});

const AssessmentResultTemplate: React.VFC<AssessmentResultTemplateProps> = props => {
  const { classes, cx } = useAssessmentResultStyles();

  return (
    (
      <StepContainer className={classes.container}>
        <div className={cx(classes.header, {
          [classes.primaryBackground]: props.color === 'primary',
          [classes.secondaryBackground]: props.color === 'secondary',
          [classes.blueBackground]: props.color === 'blue',
        })}
        >
          <div className={classes.sectionContent}>
            <Typography variant="overline">Based on your responses</Typography>
            {props.stepHeading || (
              <StepHeading>
                We recommend&nbsp;
                <Typography
                  component="span"
                  variant="inherit"
                  className={cx({
                    [classes.primaryColor]: props.color === 'primary',
                    [classes.secondaryColor]: props.color === 'secondary',
                    [classes.blueColor]: props.color === 'blue',
                  })}
                >
                  {props.careType}
                </Typography>
              </StepHeading>
            )}
          </div>
        </div>
        <WaveBg color={props.color} />
        <StepContent className={classes.section}>
          {props.children}
        </StepContent>
        <div className={classes.footer}>
          <div className={classes.sectionContent}>
            <div className={classes.subSection}>
              <Typography variant="h6">Have more questions?</Typography>
              <Typography>
                Our professional team can help you decide on the best care for you. Contact us at <a className={classes.helpLink} href="mailto:help@joinuplift.co">help@joinuplift.co</a>
              </Typography>
            </div>
          </div>
        </div>
        <StepNavigator onBack={props.onBack}>
          <Button variant="contained" color="secondary" onClick={props.onContinue} endIcon={<ArrowForward />}>continue with {props.buttonLabel ?? props.careType}</Button>
          <Button color="secondary" onClick={props.onChooseAnother}>choose another type of care</Button>
        </StepNavigator>
      </StepContainer>
    )
  );
};

const useAssessmentResultStyles = makeStyles({ name: 'AssessmentResult' })(theme => ({
  container: {
    boxSizing: 'border-box',
  },
  section: {
    padding: theme.spacing(3, 0),

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(5, 0),
    }
  },
  secondaryBackground: {
    background: theme.palette.secondary.background
  },
  primaryBackground: {
    background: theme.palette.primary.background
  },
  blueBackground: {
    background: theme.palette.blue.background,
  },
  secondaryColor: {
    color: theme.palette.secondary.main
  },
  primaryColor: {
    color: theme.palette.primary.main
  },
  blueColor: {
    color: theme.palette.blue.main
  },
  helpLink: {
    color: 'inherit',
  },
  header: {
    padding: theme.spacing(2, 0),

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3, 0),
    }
  },
  footer: {
    background: theme.palette.grey[100],
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(5, 0)
  },
  sectionContent: {
    margin: '0 auto',
    maxWidth: `calc(640px + ${theme.spacing(4)})`,
    width: '100%',
    padding: theme.spacing(0, 2)
  },
  subSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
}));
